import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import { ObjectSelect } from '@app/form';
import {
  FieldAnswer,
  FieldChoice,
  FieldType,
  MultipleChoiceField,
  SingleChoiceField,
} from 'queries';
import { useFormikContext } from 'formik';

const useStyles = createUseStyles((theme: Theme) => ({
  questionContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  questionLabel: {
    ...theme.fonts.label,
  },
  minMaxLabel: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    marginTop: theme.marginBase,
  },
  required: {
    color: theme.colors.red,
  },
  input: {
    marginTop: theme.marginBase * 2,
  },
}));

interface Props {
  isPreview?: boolean;
  field: MultipleChoiceField | SingleChoiceField;
}

const pickMinMaxLabel = (field: MultipleChoiceField, t: TFunction) => {
  if (!field.properties.choices) {
    return '';
  }
  const { minSelection, maxSelection } = field.validation || {};
  if (!minSelection && !maxSelection && maxSelection !== 0) {
    return '';
  }

  if (minSelection === maxSelection) {
    return t('screeningForm.sameOption', {
      same: maxSelection,
    });
  }

  if (!minSelection) {
    return t('screeningForm.maxOption', {
      max: maxSelection,
    });
  }

  if (!maxSelection) {
    return t('screeningForm.minOption', {
      min: minSelection,
    });
  }

  return t('screeningForm.minMaxOption', {
    min: minSelection,
    max: maxSelection,
  });
};

export const Autocomplete = ({ isPreview, field }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const choices =
    field.properties && field.properties.choices
      ? field.properties.choices
      : [];

  const { values, setFieldValue } = useFormikContext<FieldAnswer>();

  const onChange = (choice: FieldChoice | FieldChoice[]) => {
    if (Array.isArray(choice)) {
      const choices = choice;
      setFieldValue('choices', {
        refs: choices.map((c) => c.ref),
        labels: choices.map((c) => c.label),
      });
    } else {
      setFieldValue('choice', {
        ref: choice.ref,
        label: choice.label,
      });
    }
  };

  const isMulti = field.type === FieldType.MULTIPLE_CHOICE;

  return (
    <>
      <div className={classes.questionContainer}>
        <label className={classes.questionLabel}>
          {field.title || t('screeningForm.noQuestion')}{' '}
          {field.validation && field.validation.required && (
            <strong className={classes.required}>*</strong>
          )}
        </label>
        <label className={classes.minMaxLabel}>
          {isMulti && pickMinMaxLabel(field, t)}
        </label>
      </div>
      <ObjectSelect
        options={choices}
        getOptionValue={(choice) => choice.ref}
        placeholder={t('screeningForm.autocompletePlaceholder')}
        onChange={onChange}
        disabled={isPreview}
        isMulti={isMulti}
        containerClass={classes.input}
        value={values && values.choice}
      />
    </>
  );
};
